import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, Mail, Clock, RefreshCw, Trash2, MessageSquare } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

const DELETION_REASONS = [
  'Not using the app anymore',
  'Found a better alternative',
  'Too many notifications',
  'Privacy concerns',
  'App is too slow/buggy',
  'Difficult to use',
  'Not finding relevant connections',
  'Account security concerns',
  'Other'
]

export default function DeleteAccountPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [selectedReason, setSelectedReason] = useState('')
  const [notes, setNotes] = useState('')
  const [showReasonPicker, setShowReasonPicker] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleDeleteAccount = () => {
    if (window.confirm(
      'Are you absolutely sure you want to delete your account?\n\n' +
      '• All your data will be permanently deleted in 7 days\n' +
      '• You can restore your account by logging in within 7 days\n' +
      '• This action cannot be undone after 7 days'
    )) {
      confirmDeletion()
    }
  }

  const confirmDeletion = async () => {
    setIsDeleting(true)

    try {
      const reason = selectedReason || 'User requested account deletion'
      
      // TODO: Schedule account deletion via API
      console.log('Scheduling account deletion:', { reason, notes })

      alert(
        'Account Deletion Scheduled\n\n' +
        'Your account has been scheduled for deletion in 7 days. You will be automatically logged out now.\n\n' +
        'You can restore your account by logging in again within the next 7 days.'
      )
      
      // TODO: Implement logout and redirect
      // await logout()
      // navigate('/login')
    } catch (error: any) {
      console.error('Error scheduling deletion:', error)
      alert('Failed to schedule account deletion. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-poppins font-bold text-gray-900 mb-6">Delete Account</h1>
      
      {/* Warning Container */}
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center mb-6">
        <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-red-600 mb-2">Delete Your Account</h2>
        <p className="text-red-900">
          This action will permanently delete your ReferPal account and all associated data.
        </p>
      </div>

      {/* Account Info */}
      <div className="bg-gray-50 border-l-4 border-gray-400 rounded-lg p-4 mb-6">
        <div className="flex items-center mb-2">
          <Mail className="h-5 w-5 text-gray-600" />
          <span className="ml-2 text-sm text-gray-600">Account to be deleted:</span>
        </div>
        <p className="ml-7 text-base font-semibold text-gray-900">
          {user?.email || 'No email provided'}
        </p>
      </div>

      {/* What Happens */}
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">What happens when you delete your account:</h3>
        
        <div className="space-y-3">
          <div className="flex items-start">
            <Clock className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <p className="ml-3 text-gray-700">
              Your account will be scheduled for deletion in 7 days
            </p>
          </div>

          <div className="flex items-start">
            <RefreshCw className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <p className="ml-3 text-gray-700">
              You can restore your account by logging in within 7 days
            </p>
          </div>

          <div className="flex items-start">
            <Trash2 className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <p className="ml-3 text-gray-700">
              All your data will be permanently deleted after 7 days
            </p>
          </div>

          <div className="flex items-start">
            <MessageSquare className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <p className="ml-3 text-gray-700">
              Your messages, referrals, and profile information will be removed
            </p>
          </div>
        </div>
      </div>

      {/* Reason Selection */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h3 className="text-base font-semibold text-gray-900 mb-2">
          Why are you deleting your account? (Optional)
        </h3>
        
        <div className="relative">
          <button
            onClick={() => setShowReasonPicker(!showReasonPicker)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <span className={selectedReason ? 'text-gray-900' : 'text-gray-500'}>
              {selectedReason || 'Select a reason...'}
            </span>
            <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {showReasonPicker && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {DELETION_REASONS.map((reason, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setSelectedReason(reason)
                    setShowReasonPicker(false)
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <span className="text-gray-900">{reason}</span>
                  {selectedReason === reason && (
                    <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Additional Feedback */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          Additional feedback (Optional)
        </h3>
        <p className="text-sm text-gray-600 mb-3">
          Help us improve ReferPal by sharing your thoughts
        </p>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What could we have done better? Any suggestions?"
          rows={4}
          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => navigate(-1)}
          disabled={isDeleting}
          className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>

        <button
          onClick={handleDeleteAccount}
          disabled={isDeleting}
          className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isDeleting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Deleting...
            </>
          ) : (
            <>
              <Trash2 className="h-5 w-5 mr-2" />
              Delete Account
            </>
          )}
        </button>
      </div>
    </div>
  )
}
